import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@medimo/ui";
import { confirmResetPassword } from "aws-amplify/auth";
import { useForm } from "react-hook-form";
import { z } from "zod";

const ResetPasswordFormSchema = z
  .object({
    email: z.string().email({
      message: "メールアドレスを入力してください",
    }),
    code: z
      .string({
        required_error: "認証コードを入力してください",
      })
      .min(1, {
        message: "認証コードを入力してください",
      }),
    password: z
      .string({
        required_error: "パスワードを入力してください",
      })
      .min(13, {
        message:
          "パスワードは13文字以上で、英大文字・英小文字・数字・記号をすべて含めてください。",
      })
      .regex(/[0-9]+/, { message: "数字を1文字以上使用してください" })
      .regex(/[a-z]+/, { message: "小文字を1文字以上使用してください" })
      .regex(/[A-Z]+/, { message: "大文字を1文字以上使用してください" })
      .regex(/[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]+/, {
        message:
          "記号（^ $ * . [ ] { } ( ) ? \" ! @ # % & / \\ , > < ' : ; | _ ~ ` = + -）を1文字以上使用してください",
      })
      .refine((val) => val === val.trim(), {
        message: "先頭または末尾にスペースは使用できません。",
      }),
    confirmPassword: z.string({
      required_error: "新しいパスワードを再入力してください",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "パスワードが一致しません",
    path: ["confirmPassword"],
  });

export type ResetPasswordFormSchemaType = z.infer<
  typeof ResetPasswordFormSchema
>;

export const useResetPasswordForm = ({
  onResetPassword,
  defaultValues,
}: {
  onResetPassword: VoidFunction;
  defaultValues: ResetPasswordFormSchemaType;
}) => {
  const { toast } = useToast();
  const form = useForm<ResetPasswordFormSchemaType>({
    defaultValues,
    mode: "onChange",
    resolver: zodResolver(ResetPasswordFormSchema),
  });

  const onSubmit = async (values: ResetPasswordFormSchemaType) => {
    try {
      await confirmResetPassword({
        username: values.email,
        confirmationCode: values.code,
        newPassword: values.password,
      });
      onResetPassword();
      toast({
        title: "パスワードを再設定しました",
      });
    } catch (e) {
      console.error(e);
      form.setError("root", {
        message:
          "エラーが発生しました。認証コードの有効期限が切れている可能性があります。",
      });
    }
  };

  return {
    form,
    onSubmit,
  };
};
